import React, { useEffect, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";

import { ZodProvider } from "@autoform/zod";
import {
  useNotification,
  useParsed,
  useWarnAboutChange,
} from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { AutoForm } from "@/components/ui/autoform";
import { injectCustomAutoFormComponents } from "@/components/ui/autoform/components/custom";
import { Button } from "@/components/ui/button";
import { useLoadingOverlay } from "@/contexts/loading-context";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { getDirtyValues, isFormDirty } from "@/lib/utils";
import { parseRead } from "@/lib/zod/read-schema";
import { MatchVenueField } from "@/pages/matches/components/fields/match-venue-field";
import { formatKickoff, formatScore } from "@/pages/matches/lib/match-format";
import {
  MatchEditType,
  matchEditSchema,
} from "@/pages/matches/schemas/match.edit.form";
import { ReadOnlyNote } from "@/pages/sports/components/read-only-note";
import { useSportsRepository } from "@/pages/sports/hooks/use-sports-repository";

const ReadOnly = ({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) => (
  <div>
    <span className="text-muted-foreground">{label}: </span>
    <span>{value ?? "—"}</span>
  </div>
);

/** Curator edit for a match: venue, status, hide, tags, ticket link and prices. The rest is TINA's. */
export const MatchEdit = () => {
  const { getMatch, updateMatch } = useSportsRepository();
  const { id } = useParsed();
  const [values, setValues] = React.useState<MatchEditType>();
  const [source, setSource] = React.useState<Record<string, any>>();
  const { open } = useNotification();
  const { setLoading } = useLoadingOverlay();
  const { setWarnWhen, warnWhen } = useWarnAboutChange();
  const setTitle = useDocumentTitle();

  const schemaProvider = useMemo(() => new ZodProvider(matchEditSchema), []);

  useEffect(() => {
    if (!id) {
      open?.({
        type: "error",
        message: "Something went wrong. This shouldn't be happening",
      });
      return;
    }
    setLoading(true);
    getMatch(id.toString())
      .then((response) => {
        // getOne resolves the flat MatchDbDto, as in PlaysEdit.
        const raw = response as Record<string, any>;
        const data = parseRead(matchEditSchema, raw, "match") as MatchEditType;
        setSource(raw);
        setValues(data);
        setTitle(
          "Edit " +
            `${raw.homeTeam?.name ?? "?"} v ${raw.awayTeam?.name ?? "?"}`,
        );
      })
      .catch((error) => {
        console.error(error);
        open?.({
          type: "error",
          message: "Failed to get match",
          description: toHumanErrorMessage(error),
        });
      })
      .finally(() => setLoading(false));
  }, [id]);

  const initializeForm = (
    form: UseFormReturn<MatchEditType, any, MatchEditType>,
  ) => {
    form.watch((value) => {
      setWarnWhen(
        form.formState.isDirty &&
          isFormDirty(
            form.formState.dirtyFields as Record<string, unknown>,
            value as Record<string, unknown>,
            form.formState.defaultValues as Record<string, unknown>,
          ),
      );
    });
  };

  const onSubmit = async (
    allValues: MatchEditType,
    form: UseFormReturn<MatchEditType, any, MatchEditType>,
  ) => {
    const dirty = getDirtyValues(
      form.formState.dirtyFields as Record<string, unknown>,
      allValues,
    );
    setWarnWhen(false);
    await updateMatch({ ...dirty, id: id?.toString() });
  };

  return (
    <div className="flex flex-row">
      <div className="flex w-full flex-col space-y-4 p-8">
        {source && (
          <div className="space-y-1 text-sm">
            <ReadOnly label="Kickoff" value={formatKickoff(source.startDate)} />
            <ReadOnly label="State" value={source.state} />
            <ReadOnly
              label="Score"
              value={formatScore(source.homeScore, source.awayScore)}
            />
            <ReadOnly label="Round" value={source.round} />
            <ReadOnly label="Season" value={source.season} />
            <ReadOnly label="Venue source" value={source.venueSource} />
            <ReadOnlyNote />
          </div>
        )}
        {values && (
          <AutoForm
            schema={schemaProvider}
            defaultValues={values}
            onSubmit={onSubmit}
            onFormInit={initializeForm}
            {...injectCustomAutoFormComponents({
              formComponents: { matchVenue: MatchVenueField },
            })}
          >
            <BottomActionBar>
              <Button disabled={!warnWhen} type="submit">
                Submit
              </Button>
            </BottomActionBar>
          </AutoForm>
        )}
      </div>
    </div>
  );
};
